import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AuthenticatorSetup = ({ onComplete, onBack, loading }) => {
  const [secret, setSecret] = useState('');
  const [qrCodeData, setQrCodeData] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    generateSecret();
  }, []);

  const generateSecret = () => {
    // Generate a random base32 secret (browser-compatible)
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars?.charAt(Math.floor(Math.random() * chars?.length));
    }
    setSecret(secret);

    // Generate QR code data in otpauth format
    const issuer = 'MyBodyToday';
    const accountName = 'user@mybodytoday.com'; // This would be the user's email
    const qrData = `otpauth://totp/${issuer}:${accountName}?secret=${secret}&issuer=${issuer}`;
    setQrCodeData(qrData);
  };

  const handleVerify = () => {
    if (!verificationCode || verificationCode?.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    // In production, verify the code against the secret
    // For now, simulate verification
    if (verificationCode?.match(/^\d{6}$/)) {
      onComplete({ secret, method: 'authenticator' });
    } else {
      setError('Invalid verification code format');
    }
  };

  const copySecret = () => {
    navigator?.clipboard?.writeText(secret);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-foreground mb-2">Set Up Authenticator App</h2>
        <p className="text-muted-foreground">Scan the QR code with your authenticator app</p>
      </div>

      {/* Step 1: Install App */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
            1
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-2">Install an Authenticator App</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Download one of these apps if you don't have one already:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Smartphone" size={16} className="text-primary" />
                <span>Google Authenticator</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Smartphone" size={16} className="text-primary" />
                <span>Microsoft Authenticator</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Smartphone" size={16} className="text-primary" />
                <span>Authy</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Smartphone" size={16} className="text-primary" />
                <span>1Password</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2: Scan QR Code */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
            2
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-2">Scan QR Code</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Open your authenticator app and scan this QR code
            </p>
            
            {qrCodeData && (
              <div className="flex flex-col items-center gap-4">
                <div className="bg-white p-4 rounded-lg">
                  <QRCodeSVG value={qrCodeData} size={200} level="H" />
                </div>
                
                <button
                  onClick={() => setShowManualEntry(!showManualEntry)}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <Icon name="Type" size={14} />
                  {showManualEntry ? 'Hide' : 'Show'} manual entry code
                </button>

                {showManualEntry && (
                  <div className="w-full bg-muted rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-2">Manual Entry Code:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm font-mono bg-background px-3 py-2 rounded border border-border">
                        {secret}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copySecret}
                        iconName="Copy"
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Step 3: Verify */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
            3
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-2">Enter Verification Code</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Enter the 6-digit code from your authenticator app
            </p>
            <Input
              type="text"
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6));
                setError('');
              }}
              error={error}
              maxLength={6}
              className="text-center text-2xl tracking-widest font-mono"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button variant="outline" onClick={onBack} disabled={loading}>
          Back
        </Button>
        <Button
          onClick={handleVerify}
          loading={loading}
          disabled={verificationCode?.length !== 6}
          className="flex-1"
          iconName="ArrowRight"
          iconPosition="right"
        >
          Verify and Continue
        </Button>
      </div>
    </div>
  );
};

export default AuthenticatorSetup;